import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { FALLBACK_CATALOG } from "@/data/catalog";
import { FaInstagram, FaTiktok, FaYoutube, FaSpotify, FaFacebookF, FaSoundcloud, FaPinterestP, FaSnapchatGhost, FaTwitter } from "react-icons/fa";
import { ArrowLeft, ArrowUpRight, Globe, Music2, AtSign, Radio } from "lucide-react";

const API = "/api";
const ICONS = {
  instagram: FaInstagram, tiktok: FaTiktok, youtube: FaYoutube, spotify: FaSpotify,
  facebook: FaFacebookF, soundcloud: FaSoundcloud, pinterest: FaPinterestP,
  snapchat: FaSnapchatGhost, twitter: FaTwitter, website: Globe, threads: AtSign,
  clubhouse: Radio, audiomack: Music2, tidal: Music2, datpiff: Music2, reverbnation: Music2,
};
const fmt = (n) => `$${n.toFixed(2)}`;

export default function PlatformPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(FALLBACK_CATALOG);

  useEffect(() => {
    axios.get(`${API}/catalog`).then((res) => {
      if (res.data?.platforms?.length) setCatalog(res.data);
    }).catch(() => {});
  }, []);

  const platform = catalog.platforms.find((p) => p.slug === slug);
  const Icon = ICONS[slug] || FaInstagram;

  if (!platform) {
    return (
      <>
        <Header />
        <div className="flex min-h-screen flex-col items-center justify-center bg-[#050505] px-6 pt-16" data-testid="platform-not-found">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E11D2E]">404</p>
          <h1 className="mt-4 font-display text-2xl uppercase tracking-tight text-white">Platform not found</h1>
          <Link to="/" data-testid="not-found-home-link" className="mt-6 border border-[#1F1F1F] px-6 py-3 font-mono text-xs uppercase tracking-[0.14em] text-white hover:border-[#333333]">
            Back to catalog
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-[#050505] pt-16" data-testid={`platform-page-${slug}`}>
        <section className="relative overflow-hidden border-b border-[#1F1F1F] py-20 lg:py-28">
          <div className="absolute -right-40 top-0 h-[420px] w-[420px] rounded-full bg-[#E11D2E]/[0.08] blur-[140px]" />
          <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onClick={() => navigate("/", { state: { scrollTo: "#pricing" } })}
              data-testid="platform-back-link"
              className="mb-8 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-[#8A8A8A] transition-colors hover:text-white"
            >
              <ArrowLeft size={14} /> All platforms
            </motion.button>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
              <div className="flex items-center gap-4">
                <span className="flex h-14 w-14 items-center justify-center border border-[#1F1F1F] bg-[#0D0D0D]">
                  {platform.icon_url ? (
                    <img src={platform.icon_url} alt="" className="h-8 w-8 object-contain" />
                  ) : (
                    <Icon size={26} className="text-[#E11D2E]" />
                  )}
                </span>
                <div>
                  <h1 className="font-display text-3xl uppercase tracking-tight text-white sm:text-4xl">{platform.name} growth</h1>
                  <p className="mt-1 text-sm text-[#8A8A8A]">{platform.tagline || "Exact pricing. Real delivery."}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
          <div className="grid gap-4">
            {platform.services.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.06} data-testid={`platform-service-${slug}-${i}`}>
                <div className="group border border-[#1F1F1F] bg-[#0D0D0D] transition-all duration-500 hover:border-[#333333] hover:shadow-[0_0_40px_rgba(225,29,46,0.08)]">
                    <div className="flex flex-wrap items-center justify-between gap-4 p-6">
                      <div className="flex flex-wrap items-baseline gap-x-3">
                        <h3 className="text-base font-600 text-white">{s.name}</h3>
                        <span className="font-mono text-xs text-[#E11D2E]" data-testid={`platform-service-qty-${s.id}`}>
                          {s.quantity.toLocaleString("en-US")} {s.unit}
                        </span>
                      </div>
                      <div className="flex items-center gap-6">
                        <span className="font-mono text-xl font-600 text-white">{fmt(s.price)}</span>
                        <Link
                          to={`/order/${s.id}`}
                          data-testid={`order-btn-${s.id}`}
                          aria-label={`Order ${s.name}`}
                          className="flex items-center gap-2 bg-[#E11D2E] px-5 py-2.5 font-mono text-xs font-600 uppercase tracking-[0.14em] text-white transition-all duration-300 hover:bg-[#F13B47] hover:shadow-[0_0_24px_rgba(225,29,46,0.35)]"
                        >
                          Order <ArrowUpRight size={13} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </Reveal>
            ))}
          </div>

          <Reveal className="mt-16">
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-[#8A8A8A]">Other networks</p>
            <div className="mt-4 flex flex-wrap gap-3">
              {catalog.platforms.filter((p) => p.slug !== slug && !p.coming_soon).map((p) => (
                <Link
                  key={p.slug}
                  to={`/services/${p.slug}`}
                  data-testid={`other-platform-${p.slug}`}
                  className="border border-[#1F1F1F] px-4 py-2 font-mono text-xs uppercase tracking-[0.14em] text-[#8A8A8A] transition-colors hover:border-[#E11D2E] hover:text-white"
                >
                  {p.name}
                </Link>
              ))}
            </div>
          </Reveal>
        </section>
      </div>
      <Footer />
    </>
  );
}
